import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { NoAccessPage } from "@/pages/clients/NoAccessPage";
import { useLegacyAccountAnchor } from "@/hooks/useLegacyAccountAnchor";
import { anchorTargetPath, type AccountAnchorTarget } from "@/lib/clientRoutes";

const ANCHOR_TARGETS: readonly AccountAnchorTarget[] = [
  "overview",
  "activities",
  "contracts",
  "complaints",
  "profile",
  "entities",
  "legal-entity",
];

function parseTarget(raw: string | null): AccountAnchorTarget | null {
  if (raw && (ANCHOR_TARGETS as readonly string[]).includes(raw)) {
    return raw as AccountAnchorTarget;
  }
  // Back-compat: F3 task-source links used the old `?tab=activity` shape.
  if (raw === "activity") return "activities";
  return null;
}

/**
 * A-Block A0/A4 — legacy /account(s)/:id → holding anchor.
 *
 * Resolves the account to its active holding and redirects (history-replace).
 * A4 RE-ORIENTATION: the DEFAULT target is now the holding-grain Client360 card
 * (AC-A4-02), not the legal-entity sub-page. Feature CTAs may request a specific
 * Client360 tab via `?to=` (activities | contracts | complaints | profile |
 * entities | legal-entity). No active membership / forbidden / not-found →
 * no-access page — NEVER an arbitrary holding.
 */
export function LegacyAccountRedirect() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const { data, isLoading, isError } = useLegacyAccountAnchor(id);

  if (!id) return <NoAccessPage reason="missing-id" />;
  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <NoAccessPage reason="lookup-error" />;

  if (data?.resolved && data.holdingId && data.accountId) {
    const to = parseTarget(search.get("to") ?? search.get("tab"));
    return (
      <Navigate to={anchorTargetPath(data.holdingId, data.accountId, to)} replace />
    );
  }
  return <NoAccessPage reason={data?.reason} />;
}
